import { Center, Group, Stack, Title } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { InformationRequestStatus } from "@tfg/db"
import { Card, Loader, ErrorBox, Pagination, FloatingButton } from "@tfg/ui"
import { useMemo, useState, memo, useEffect } from "react"
import { useTranslation } from "react-i18next"

import { getTraceabilityRequestsQueryParams } from "@tfg/types/schemas/traceabilityRequests"

import { queryClient } from "~/backend/queryClient"
import { FindOperationChainsResponseDto } from "~/gen/api"
import { BatchModal } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/BatchModal/BatchModal.tsx"
import { BatchUpdateModalProvider } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/BatchModal/store/BatchModalContext.tsx"
import { SubmitRequestsModal } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/SubmitRequestsModal.tsx"
import { RequestFilters } from "~/pages/SupplierPortal/TraceabilityRequests/components/RequestFilters"
import { RequestSearch } from "~/pages/SupplierPortal/TraceabilityRequests/components/RequestSearch"
import { RequestSorts } from "~/pages/SupplierPortal/TraceabilityRequests/components/RequestSorts"
import { TraceabilityRequest } from "~/pages/SupplierPortal/TraceabilityRequests/components/TraceabilityRequest.tsx"
import { useListTraceabilityRequests } from "~/pages/SupplierPortal/TraceabilityRequests/hooks"
import { requestsToRequestsWithOp } from "~/pages/SupplierPortal/TraceabilityRequests/utils.ts"
import { useAuth } from "~/store/auth-context"

const REQUESTS_PER_PAGE = 5

export type CheckList = Record<number, boolean>
interface Props {
  operationChains: FindOperationChainsResponseDto[]
}

export const TraceabilityRequestsList = memo(({ operationChains }: Props) => {
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: REQUESTS_PER_PAGE,
  })
  const { user } = useAuth()

  const [filters, setFilters] = useState<
    Exclude<getTraceabilityRequestsQueryParams["filters"], undefined>
  >({
    status: InformationRequestStatus.OPENED,
  })
  const [sorts, setSorts] = useState<
    Exclude<getTraceabilityRequestsQueryParams["sort"], undefined>
  >({})
  const {
    data,
    isPending: isRequestsLoading,
    isError: isRequestsError,
  } = useListTraceabilityRequests({
    filters,
    pagination,
    sort: sorts,
  })

  const requests = useMemo(
    () =>
      user
        ? requestsToRequestsWithOp({
            requests: data?.data ?? [],
            operationChains,
            user,
          })
        : [],
    [data?.data, operationChains, user]
  )

  const { t } = useTranslation(["traceability"])

  const [openedSubmit, submitHandlers] = useDisclosure()
  const [irsToSubmit, setIrsToSubmit] = useState<Set<number>>(new Set<number>())

  useEffect(() => {
    setIrsToSubmit(new Set<number>())
  }, [pagination.page])

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }))
  }, [filters])

  if (isRequestsError) {
    return <ErrorBox title={t("request.errors.generic")} error={null} />
  }

  return (
    <BatchUpdateModalProvider>
      <Stack
        bg="var(--mantine-color-supplier-header-bg)"
        gap="xs"
        w="100%"
        m={0}
        mih="100vh"
      >
        <Stack
          bg="var(--mantine-color-supplier-header-bg)"
          style={{
            position: "sticky",
            top: "calc(var(--mantine-supplier-header-height) - var(--tfg-layout-padding-y) )",
            zIndex: "99",
            borderBottom: "1px solid var(--mantine-color-gray-2)",
          }}
          py="var(--tfg-molecule-padding-y)"
          px="var(--tfg-molecule-padding-x)"
        >
          <Stack gap="xs">
            <Group justify="space-between">
              <Group gap="xs">
                <RequestSearch filters={filters} setFilters={setFilters} />
                <RequestSorts
                  sorts={sorts}
                  setSorts={setSorts}
                  filters={filters}
                />
                <RequestFilters filters={filters} setFilters={setFilters} />
              </Group>
              <Pagination
                value={pagination.page + 1}
                onChange={(page) =>
                  setPagination((prev) => ({ ...prev, page: page - 1 }))
                }
                total={Math.ceil((data?.total ?? 0) / REQUESTS_PER_PAGE)}
              />
            </Group>
          </Stack>
        </Stack>
        {isRequestsLoading ? (
          <Loader fullPage />
        ) : requests.length ? (
          <Stack gap="xl">
            {requests.map((request, index) => (
              <Card key={request.original.id} shadow="xs" bg="gray.0">
                <TraceabilityRequest
                  initialData={request}
                  operationChains={operationChains}
                  index={index}
                  setIrsToSubmit={setIrsToSubmit}
                />
              </Card>
            ))}
            <FloatingButton
              onClick={() => {
                queryClient.invalidateQueries({
                  queryKey: ["listTraceabilityRequests"],
                })
                submitHandlers.open()
              }}
              tooltip={t("request.completeTooltip")}
              label={t("request.complete")}
            />
          </Stack>
        ) : (
          <Center pt="xl">
            <Title order={3}>
              {filters?.status === InformationRequestStatus.CLOSED
                ? t("requests.noRequests.closed")
                : t("requests.noRequests.open")}
            </Title>
          </Center>
        )}
        {filters?.status === InformationRequestStatus.OPENED && (
          <>
            <BatchModal />
            <SubmitRequestsModal
              operationChains={operationChains}
              opened={openedSubmit}
              onClose={() => {
                submitHandlers.close()
              }}
              informationRequestIds={Array.from(irsToSubmit)}
              onRequestsSubmitted={() => {
                queryClient.invalidateQueries({
                  queryKey: ["listTraceabilityRequests"],
                })
                setIrsToSubmit(new Set<number>())
              }}
            />
          </>
        )}
      </Stack>
    </BatchUpdateModalProvider>
  )
})
